import './index.scss'
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { Modal, Tree, Checkbox, Input, Tag } from 'antd'
import {
  useAppDispatch,
  useAppSelector,
  useGroupID,
  useSensorsRole,
  useUserId
} from '../../../app/hooks'
import { produce } from 'immer'
import classNames from 'classnames'
import { TodayIconFont as IconFont } from '../IconFonts'
import {
  findChildrenDepartmentIDs,
  findUserIDsInDepartment
} from '../../department/componentsV2'
import { DepartmentV2, DepartmentMemberV2 } from '../../../global'
import CONFIG from '../../../constants/config'
import sensors, { parsePlaceValue } from '../../../utils/sensors'
import crown_golden_img from '../../../images/crown_golden.png'
import ModalFooter from './ModalFooter'
import VipTip from './VipTip'
import { useOnPay } from './hooks'
import { fetchGroupDeparmentByLevel } from '../../../actions/group'
import { useDepartmentAuth } from '../../../app/authPolicyHooks'
import { AntTreeNode } from 'antd/lib/tree/Tree'
import SelectedDepartments from './components/SelectedDepartments'
import SearchMemberResult from './components/SearchMemberResult'
import { setFilter } from '../../../actions/photo'
import FilteredHistory from './components/FilteredHistory'
import { useFilterMemberHistory } from '../../../app/hooks/useFilterMemberHistory'
import { CaretDownOutlined } from '@ant-design/icons'

const { TreeNode } = Tree
const Search = Input.Search

export type SelectedDepartment = {
  id: string
  name: string
  num: number
}

export type FilterObj = {
  departmentUsers: DepartmentMemberV2[] //回显数据：已选部门成员
  departments: SelectedDepartment[] //回显数据：已选部门
}

interface IDepartmentMemberPicker {
  open: boolean
  title?: string
  onCancel: () => void
  onOk: (params: {
    selectedDepartmentUsers: DepartmentMemberV2[]
    selectedDepartments: SelectedDepartment[]
  }) => void
  searchRef?: any
  filterObj: FilterObj
  vipRequired?: boolean //仅VIP可提交
  limitedTimeFree?: boolean //限时免费
  onlyInGroup?: boolean //仅显示当前在团队成员
  dataStatisticsType?: string //数据统计类型
  onlyRootDepartments?: boolean //仅允许选择总部门和成员，子部门禁止勾选
}

const DepartmentUserPicker: React.FC<IDepartmentMemberPicker> = (props) => {
  const {
    title,
    open,
    onCancel,
    onOk,
    filterObj,
    vipRequired,
    limitedTimeFree,
    onlyInGroup,
    dataStatisticsType,
    onlyRootDepartments
  } = props
  const dispatch = useAppDispatch()
  const filter = useAppSelector((state) => state.photo.filter)
  const organization: DepartmentV2 = useAppSelector(
    (state) => state.organization.pickerOrganization
  )
  const isOnlyInGroup = useAppSelector((state) => state.group.onlyInGroup) as
    | 0
    | 1
    | 2
  const groupID = useGroupID()
  const userID = useUserId()
  const departmentAuth = useDepartmentAuth()
  const members = useAppSelector((state) => state.group.members)
  const searchRef: React.MutableRefObject<any> = useRef()
  const isVip = useAppSelector((state) => state.group.isVip)
  const onPay = useOnPay()
  const sensorsRole = useSensorsRole()
  const [loadedKeys, setLoadedKeys] = useState<string[]>([])
  const [expandedKeys, setExpandedKeys] = useState<string[]>([])
  const groupAuthPolicy = useAppSelector((state) => state.group.groupAuthPolicy)
  const { history: filteredHistory, addHistory: addSearchHistory } =
    useFilterMemberHistory()

  const searchUserResultRef = useRef<any>(null)

  //顶级部门是否禁用
  //当不是查看全部成员的权限，但部门管理员在多个部门下时，仍会显示团队节点
  //当显示团队节点，且符合上述条件时，团队节点禁用
  const rootGroupDisabled = useMemo(() => {
    return (
      organization?.id === '0' &&
      departmentAuth?.departmentIDList?.every((id) => id !== 0)
    )
  }, [organization, departmentAuth])

  useEffect(() => {
    // userID / groupID 为空时直接返回：
    // 这两个字段分别来自 fetchUser、setGroupId（依赖 fetchUserPoliceList），
    // 与 departmentAuth 异步并发。如果 effect 触发时 userID 还是空，body 里
    // userID:"" 会被后端 verify error 返 {code:401}，触发误弹"登录状态失效"。
    if (!userID || !groupID) return
    fetchDepartment()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentAuth, userID, groupID])

  // organization 加载后设置默认展开根节点
  useEffect(() => {
    if (organization && expandedKeys.length === 0) {
      setExpandedKeys([String(organization.id)])
    }
  }, [organization])

  const fetchDepartment = (parentDepartmentID?: string) => {
    const params = {
      groupID: groupID,
      userID: userID,
      enableQuitUser: isOnlyInGroup === CONFIG.onlyInGroup.ALL
    }
    if (parentDepartmentID) {
      params['departmentID'] = parentDepartmentID
      params['departmentIDList'] = [Number(parentDepartmentID)]
    } else if (departmentAuth) {
      params['departmentIDList'] = departmentAuth.departmentIDList
      params['userIDList'] = departmentAuth.userIDList
    }
    return dispatch(fetchGroupDeparmentByLevel(params))
  }

  //选择的部门列表，如果选择了父级部门，子级部门不能被选
  const [selectedDepartments, setSelectedDepartments] = useState<
    SelectedDepartment[]
  >([])
  //从组织架构中选择的成员列表
  const [selectedDepartmentUsers, setSelectedDepartmentUsers] = useState<
    // DepartmentMember[]
    DepartmentMemberV2[]
  >([])

  const [searchValue, setSearchValue] = useState<string>('')

  useEffect(() => {
    setSearchValue('')
  }, [open])

  const normalizedFilterObj = useMemo<FilterObj>(() => {
    return {
      departmentUsers: filterObj?.departmentUsers ?? [],
      departments: filterObj?.departments ?? []
    }
  }, [filterObj])

  useEffect(() => {
    //数据回显
    const { departmentUsers, departments } = normalizedFilterObj
    setSelectedDepartmentUsers(departmentUsers as DepartmentMemberV2[])
    setSelectedDepartments(departments as SelectedDepartment[])
  }, [normalizedFilterObj, open, onlyInGroup])

  useEffect(() => {
    if (open) {
      sensors.track('web_workgroup_filter_member_page_click', {
        clickItem: 'enterPage',
        pagePath: dataStatisticsType
          ? dataStatisticsType
          : window.location.pathname,
        historyRecordNum: filteredHistory?.length || 0
      })
    }
  }, [open])

  const onCancelClick = useCallback(() => {
    setSearchValue('')
    onCancel()
    sensors.track('web_workgroup_filter_member_page_click', {
      clickItem: 'cancel',
      pagePath: dataStatisticsType
        ? dataStatisticsType
        : window.location.pathname,
      groupID: groupID
    })
  }, [])

  const onOkClick = useCallback(() => {
    const selectedDepartmentUserIDs = selectedDepartmentUsers.map(
      (user) => user.userID
    )

    const isWholeGroup =
      selectedDepartments.some((department) => department.id === '0') ||
      (selectedDepartments.length === 0 &&
        selectedDepartmentUserIDs.length === 0)
    if (vipRequired && !limitedTimeFree && !isVip && !isWholeGroup) {
      sensors.track('web_click_button_workgroup_go_vip_trigger', {
        place: parsePlaceValue(),
        functionType: 'chooseMember',
        groupID
      })
      onPay()
      return
    }

    // 添加筛选历史
    addSearchHistory(selectedDepartments, selectedDepartmentUsers)

    setSearchValue('')
    onOk({
      selectedDepartmentUsers,
      selectedDepartments
    })
    sensors.track('web_workgroup_filter_member_page_click', {
      clickItem: 'done',
      pagePath: dataStatisticsType
        ? dataStatisticsType
        : window.location.pathname,
      groupID: groupID,
      memberNum: selectedDepartmentUserIDs.length,
      departmentNum: selectedDepartments.length,
      content: searchValue
    })

    const userNumInDepartments = selectedDepartments
      .map((department) => department.num)
      .reduce((acc, cur) => acc + cur, 0)
    const total = selectedDepartmentUserIDs.length + userNumInDepartments
    let onJobNum = 0,
      quitJobNum = 0
    if (
      total > 0 &&
      total !== members.length &&
      selectedDepartments.every((department) => department.id !== '0')
    ) {
      onJobNum = selectedDepartmentUsers.filter((user) => !user.quit).length
      quitJobNum = selectedDepartmentUsers.filter((user) => user.quit).length
    }
    sensors.track('web_click_page_workgroup_filter_members', {
      onJobSwitch: isOnlyInGroup === 2 ? true : false,
      onJobNum,
      quitJobNum,
      place: parsePlaceValue(),
      role: sensorsRole,
      groupID: groupID
    })
  }, [
    selectedDepartmentUsers,
    selectedDepartments,
    vipRequired,
    limitedTimeFree,
    isVip
  ])

  /**
   * 取消选择成员时，从2个已选成员列表中移除此成员
   * @param userID
   */
  const removeUserFromSelectedUsers = useCallback(
    (userID: string): void => {
      const selectedDepartmentUsersIndex = selectedDepartmentUsers.findIndex(
        (selectedDepartmentUser) => selectedDepartmentUser.userID === userID
      )

      if (selectedDepartmentUsersIndex > -1) {
        setSelectedDepartmentUsers(
          produce(selectedDepartmentUsers, (draft) => {
            draft.splice(selectedDepartmentUsersIndex, 1)
          })
        )
      }
    },
    [selectedDepartmentUsers]
  )

  /**
   * 创建组织架构下的成员节点列表
   * @param users 要生成的成员节点
   * @param checkboxDisabled 如果父级部门已选中，则成员节点也选中，并且禁止操作
   * @returns 成员节点列表
   */
  const generateUserNodes = useCallback(
    (users: DepartmentMemberV2[], checkboxDisabled: boolean) => {
      //仅显示在团队成员，权限最高，覆盖用户开关
      //是否展示已退出成员，普通用户是接口过滤返回，组织架构下用户需要前端过滤
      //过滤掉未激活的成员
      return users
        .filter((user) => {
          if (
            onlyInGroup ||
            isOnlyInGroup === CONFIG.onlyInGroup.ONLY_IN_GROUP ||
            // 团队成员信息权限为仅查看自己，且用户不是管理员（是普通成员、部门管理员），不显示已退出成员
            ((groupAuthPolicy?.roleID === 0 || groupAuthPolicy?.roleID === 3) &&
              groupAuthPolicy?.groupDepartmentView !== 0)
          ) {
            return !user.quit
          } else {
            return true
          }
        })
        .map((user) => {
          const selected = selectedDepartmentUsers.some(
            (selectedUser) => selectedUser.userID === user.userID
          )
          const title = (
            <span className="treenode-title">
              <span className="name">
                {user.quit && <Tag>已退出</Tag>}
                {user.name}
              </span>
              <Checkbox
                checked={selected || checkboxDisabled} //如果父级部门已选中，则成员节点也选中，并且禁止操作
                disabled={checkboxDisabled} //如果父级部门已选中，则成员节点也选中，并且禁止操作
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedDepartmentUsers(
                      produce(selectedDepartmentUsers, (draft) => {
                        if (!selected) {
                          draft.push(user)
                        }
                      })
                    )
                    sensors.track('web_workgroup_filter_member_page_click', {
                      clickItem: 'selectOneMember',
                      memberStatus: user.quit ? 'offJob' : 'onJob',
                      pagePath: dataStatisticsType
                        ? dataStatisticsType
                        : window.location.pathname,
                      groupID: groupID,
                      content: searchValue
                    })
                  } else {
                    removeUserFromSelectedUsers(user.userID)
                    sensors.track('web_workgroup_filter_member_page_click', {
                      clickItem: 'unSelectOneMember',
                      memberStatus: user.quit ? 'offJob' : 'onJob',
                      pagePath: dataStatisticsType
                        ? dataStatisticsType
                        : window.location.pathname,
                      groupID: groupID
                    })
                  }
                }}
              />
            </span>
          )
          return {
            icon: (
              <img
                className="headimg"
                src={user.avatar + '?x-oss-process=image/resize,w_48'}
              />
            ),
            title,
            key: user.userID,
            isLeaf: true
          }
        })
    },
    [onlyInGroup, isOnlyInGroup, selectedDepartmentUsers]
  )

  /**
   *
   * @param departments 要生成的组织架构树
   * @param parentIsSelected 父级是否已选中
   * @param checkboxDisabled 禁止选中
   * @returns
   */
  const generateTreeNodes = useCallback(
    (
      departments: DepartmentV2[],
      parentIsSelected: boolean,
      checkboxDisabled: boolean
    ): any => {
      return departments.map((department) => {
        const selected = selectedDepartments.some(
          (selectedDepartment) => selectedDepartment.id === department.id
        )

        const shouldSelected = selected || parentIsSelected //如果父级已选中，则子节点也选中

        // 判断是否为子部门 - 只允许选择根部门(id为'0')
        const isSubDepartment = department.id !== '0'
        // 子部门禁用勾选 - 当onlyRootDepartments为true时，禁用所有子部门的勾选
        const isDepartmentDisabled =
          checkboxDisabled ||
          parentIsSelected ||
          (onlyRootDepartments && isSubDepartment)

        const title = (
          <span className="treenode-title">
            <span className="name">
              {department.name}({department.num}人)
            </span>
            <Checkbox
              checked={shouldSelected}
              disabled={isDepartmentDisabled} //如果父级已选中或是子部门且onlyRootDepartments为true，则禁止操作
              onChange={(e) => {
                if (e.target.checked) {
                  if (department.id === '0') {
                    setSelectedDepartments([
                      {
                        id: department.id,
                        name: department.name,
                        num: department.num
                      }
                    ])
                    setSelectedDepartmentUsers([])
                    sensors.track('web_workgroup_filter_member_page_click', {
                      clickItem: 'selectAll',
                      pagePath: dataStatisticsType
                        ? dataStatisticsType
                        : window.location.pathname,
                      groupID: groupID
                    })
                  } else {
                    //选中部门
                    //检查已选择部门列表中是否包含子孙级节点，如果存在则过滤掉子孙级节点，只保留当前部门节点
                    setSelectedDepartments(
                      produce(selectedDepartments, (draft) => {
                        const departmentChildIDs =
                          findChildrenDepartmentIDs(department)
                        const mDraft = draft.filter(
                          (selectedDepartment) =>
                            !departmentChildIDs.includes(selectedDepartment.id)
                        )
                        if (!selected) {
                          mDraft.push({
                            id: department.id,
                            name: department.name,
                            num: department.num
                          })
                        }
                        return mDraft
                      })
                    )
                    //检查已选择部门成员列表中是否包含此部门下的成员，如果存在则过滤掉，只保留当前部门节点
                    setSelectedDepartmentUsers(
                      produce(selectedDepartmentUsers, (draft) => {
                        const userIDs = findUserIDsInDepartment(department)
                        const mDraft = draft.filter(
                          (selectedDepartmentUser) =>
                            !userIDs.includes(selectedDepartmentUser.userID)
                        )
                        return mDraft
                      })
                    )
                  }
                  sensors.track('web_workgroup_filter_member_page_click', {
                    clickItem: 'selectOneDepartment',
                    pagePath: dataStatisticsType
                      ? dataStatisticsType
                      : window.location.pathname,
                    groupID: groupID,
                    content: searchValue
                  })
                } else {
                  setSelectedDepartments(
                    produce(selectedDepartments, (draft) => {
                      draft.splice(
                        draft.findIndex(
                          (selectedDepartment) =>
                            selectedDepartment.id === department.id
                        ),
                        1
                      )
                    })
                  )
                  sensors.track('web_workgroup_filter_member_page_click', {
                    clickItem: 'unSelectOneDepartment',
                    pagePath: dataStatisticsType
                      ? dataStatisticsType
                      : window.location.pathname,
                    groupID: groupID
                  })
                  if (department.id === '0') {
                    sensors.track('web_workgroup_filter_member_page_click', {
                      clickItem: 'unSelectAll',
                      pagePath: dataStatisticsType
                        ? dataStatisticsType
                        : window.location.pathname,
                      groupID: groupID
                    })
                  }
                }
              }}
            />
          </span>
        )
        const childNodes: any[] = []
        if (department.children && department.children.length > 0) {
          childNodes.push(
            ...generateTreeNodes(department.children, shouldSelected, false)
          )
        }
        if (department.users && department.users.length > 0) {
          childNodes.push(
            ...generateUserNodes(department.users, shouldSelected)
          )
        }

        return {
          icon: <IconFont type="icondepartment" style={{ color: '#0093FF' }} />,
          isLeaf: department.num === 0,
          title,
          key: department.id,
          children: childNodes.length > 0 ? childNodes : undefined
        }
      })
    },
    [selectedDepartments, selectedDepartmentUsers, generateUserNodes]
  )

  // loadDepartmentData 已被 onExpand 替代，不再需要

  const selectAll = () => {
    sensors.track('web_workgroup_filter_member_page_click', {
      clickItem: 'selectAllText',
      pagePath: dataStatisticsType
        ? dataStatisticsType
        : window.location.pathname,
      groupID: groupID
    })
    if (searchValue.trim()) {
      if (searchUserResultRef?.current) {
        searchUserResultRef.current.selectAllUsers()
      }
    } else {
      setSelectedDepartments([
        {
          id: organization.id,
          name: organization.name,
          num: organization.num
        }
      ])
      setSelectedDepartmentUsers([])
    }
  }

  const clearAll = () => {
    setSelectedDepartments([])
    setSelectedDepartmentUsers([])
  }

  return (
    <Modal
      wrapClassName={classNames('department-members-picker-modal-v2', {
        'vip-required': vipRequired && !limitedTimeFree && !isVip
      })}
      centered
      open={open}
      getContainer={false}
      title={
        <>
          {title || '筛选成员'}
          {limitedTimeFree && (
            <span className="limited-time-free">免费体验</span>
          )}
          {vipRequired && (
            <img className="title-vip-icon" src={crown_golden_img} />
          )}
        </>
      }
      width={600}
      styles={{ body: { width: '600px', height: '450px' } }}
      onCancel={onCancelClick}
      footer={
        <ModalFooter
          onCancel={onCancelClick}
          onOk={onOkClick}
          onlyInGroup={onlyInGroup}
          onIsOnlyInGroupChange={() => {
            setLoadedKeys([])
            setSelectedDepartmentUsers([])
            setSelectedDepartments([])
            dispatch(
              setFilter({
                ...filter,
                departments: [],
                departmentUsers: []
              })
            )
          }}
        />
      }
    >
      <VipTip
        vipRequired={vipRequired}
        limitedTimeFree={limitedTimeFree}
        modalOpen={open}
      />
      <div className="modal-content ">
        <div className="source">
          <h3>
            <span>选择</span>
            <span className="select-all" onClick={selectAll}>
              全选
            </span>
          </h3>
          <div className="department-container">
            <div className="search-warp">
              <Search
                size="small"
                allowClear
                enterButton
                placeholder="请输入姓名/手机号搜索"
                ref={searchRef}
                value={searchValue}
                style={{ width: '228px', margin: '10px' }}
                onChange={(e) => setSearchValue(e.target.value)}
                onSearch={(value) => {
                  setSearchValue(value)
                  sensors.track('web_workgroup_filter_member_page_click', {
                    clickItem: 'searchMember',
                    pagePath: dataStatisticsType
                      ? dataStatisticsType
                      : window.location.pathname,
                    groupID: groupID,
                    content: value
                  })
                }}
              />
              <FilteredHistory
                selectedDepartments={selectedDepartments}
                setSelectedDepartments={setSelectedDepartments}
                selectedDepartmentUsers={selectedDepartmentUsers}
                setSelectedDepartmentUsers={setSelectedDepartmentUsers}
              />
            </div>
            <div className="tree-wrap">
              {searchValue.trim() && (
                <SearchMemberResult
                  ref={searchUserResultRef}
                  searchName={searchValue}
                  setSelectedDepartmentUsers={setSelectedDepartmentUsers}
                  selectedDepartmentUsers={selectedDepartmentUsers}
                  removeUserFromSelectedUsers={removeUserFromSelectedUsers}
                  searchValue={searchValue}
                />
              )}

              {
                //为了让defaultExpandedKeys生效，需要有数据后再渲染树，否则树从无到有进行更新，defaultExpandedKeys将不生效
                !searchValue.trim() && organization && (
                  <Tree
                    showIcon
                    blockNode
                    multiple={false}
                    selectable={false}
                    expandedKeys={expandedKeys}
                    onExpand={(keys, { node, expanded }) => {
                      setExpandedKeys(keys as string[])
                      // 展开时，如果该节点没有子数据，异步加载
                      if (expanded && !node.children?.length && !node.isLeaf) {
                        const departmentID = node.key as string
                        if (!loadedKeys.includes(departmentID)) {
                          fetchDepartment(departmentID).then(() => {
                            setLoadedKeys((prev) => [...prev, departmentID])
                          })
                        }
                      }
                    }}
                    switcherIcon={
                      <CaretDownOutlined style={{ fontSize: '14px' }} />
                    }
                    treeData={generateTreeNodes(
                      [organization],
                      false,
                      !!rootGroupDisabled
                    )}
                  />
                )
              }
            </div>
          </div>
        </div>
        <div className="target">
          <h3>
            <span>已选</span>
            <span className="clear-all" onClick={clearAll}>
              清空
            </span>
          </h3>
          <div className="department-container">
            <SelectedDepartments
              selectedDepartments={selectedDepartments}
              setSelectedDepartments={setSelectedDepartments}
              selectedDepartmentUsers={selectedDepartmentUsers}
              removeUserFromSelectedUsers={removeUserFromSelectedUsers}
            />
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default DepartmentUserPicker
